/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { RenditionEntry } from '../model/renditionEntry';
import { RenditionPaging } from '../model/renditionPaging';
import { SharedLinkEntry } from '../model/sharedLinkEntry';
import { SharedLinkPaging } from '../model/sharedLinkPaging';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
import { buildCollectionParam } from '../../../alfrescoApiClient';
export class SharedlinksApi extends BaseApi {
    createSharedLink(sharedLinkBodyCreate, opts) {
        throwIfNotDefined(sharedLinkBodyCreate, 'sharedLinkBodyCreate');
        opts = opts || {};
        const postBody = sharedLinkBodyCreate;
        const pathParams = {};
        const queryParams = {
            'include': buildCollectionParam(opts['include'], 'csv'),
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/shared-links', 'POST', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SharedLinkEntry);
    }
    deleteSharedLink(sharedId) {
        throwIfNotDefined(sharedId, 'sharedId');
        const postBody = null;
        const pathParams = {
            'sharedId': sharedId
        };
        const queryParams = {};
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/shared-links/{sharedId}', 'DELETE', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts);
    }
    emailSharedLink(sharedId, sharedLinkBodyEmail) {
        throwIfNotDefined(sharedId, 'sharedId');
        throwIfNotDefined(sharedLinkBodyEmail, 'sharedLinkBodyEmail');
        const postBody = sharedLinkBodyEmail;
        const pathParams = {
            'sharedId': sharedId
        };
        const queryParams = {};
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/shared-links/{sharedId}/email', 'POST', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts);
    }
    getSharedLink(sharedId, opts) {
        throwIfNotDefined(sharedId, 'sharedId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'sharedId': sharedId
        };
        const queryParams = {
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/shared-links/{sharedId}', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SharedLinkEntry);
    }
    getSharedLinkContent(sharedId, opts) {
        throwIfNotDefined(sharedId, 'sharedId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'sharedId': sharedId
        };
        const queryParams = {
            'attachment': opts['attachment']
        };
        const headerParams = {
            'If-Modified-Since': opts['ifModifiedSince'], 'Range': opts['range']
        };
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/octet-stream'];
        return this.apiClient.callApi('/shared-links/{sharedId}/content', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, 'blob');
    }
    getSharedLinkRendition(sharedId, renditionId) {
        throwIfNotDefined(sharedId, 'sharedId');
        throwIfNotDefined(renditionId, 'renditionId');
        const postBody = null;
        const pathParams = {
            'sharedId': sharedId, 'renditionId': renditionId
        };
        const queryParams = {};
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/shared-links/{sharedId}/renditions/{renditionId}', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, RenditionEntry);
    }
    getSharedLinkRenditionContent(sharedId, renditionId, opts) {
        throwIfNotDefined(sharedId, 'sharedId');
        throwIfNotDefined(renditionId, 'renditionId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'sharedId': sharedId, 'renditionId': renditionId
        };
        const queryParams = {
            'attachment': opts['attachment']
        };
        const headerParams = {
            'If-Modified-Since': opts['ifModifiedSince'], 'Range': opts['range']
        };
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/octet-stream'];
        return this.apiClient.callApi('/shared-links/{sharedId}/renditions/{renditionId}/content', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, 'blob');
    }
    listSharedLinkRenditions(sharedId) {
        throwIfNotDefined(sharedId, 'sharedId');
        const postBody = null;
        const pathParams = {
            'sharedId': sharedId
        };
        const queryParams = {};
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/shared-links/{sharedId}/renditions', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, RenditionPaging);
    }
    listSharedLinks(opts) {
        opts = opts || {};
        const postBody = null;
        const pathParams = {};
        const queryParams = {
            'skipCount': opts['skipCount'],
            'maxItems': opts['maxItems'],
            'where': opts['where'],
            'include': buildCollectionParam(opts['include'], 'csv'),
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/shared-links', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SharedLinkPaging);
    }
}
